<script setup lang="ts">
import { computed, ref } from 'vue';
import { useQuasar } from 'quasar';
import { useI18n } from 'vue-i18n';
import { schoolUserDisplayName } from 'src/modules/School/utilities/schoolUserDisplayName';
import { isInactiveEnrollment } from 'src/modules/School/utilities/assignmentOptions';
import { hasStartedProgress } from 'src/modules/School/utilities/assignmentProgress';
import {
  mdiAccountMinusOutline,
  mdiAccountOffOutline,
  mdiAccountPlusOutline,
  mdiChevronDown,
  mdiChevronUp,
  mdiEyeOutline,
  mdiSort,
  mdiSortAlphabeticalAscending,
  mdiSortAlphabeticalDescending,
} from '@quasar/extras/mdi-v7';

export interface EntityOption {
  documentId: string;
  name: string;
  assignedCount?: number;
  completedCount?: number;
}

export interface EntityProgressRow {
  student?: {
    documentId?: string;
    username?: string;
    email?: string;
    firstname?: string;
    lastname?: string;
  };
  enrollmentStatus?: string;
  assigned?: boolean;
  userProgress?: {
    documentId?: string;
    score?: number;
    timeAccumulator?: number;
    startDate?: string;
    completionDate?: string;
    progress?: Record<string, unknown> | null;
  } | null;
}

const props = withDefaults(
  defineProps<{
    entities: EntityOption[];
    totalAssigned: number;
    search: string;
    isExpanded: boolean;
    selectedDocumentId: string;
    selectedName?: string;
    progressRows: EntityProgressRow[];
    isProgressLoading: boolean;
    /** i18n key for the list heading (e.g. COURSES / SECTIONS). */
    titleKey: string;
    /** i18n key used as the selected-entity fallback label. */
    selectedFallbackKey: string;
    /** i18n key for the "select an assigned entity to view progress" banner. */
    selectPromptKey: string;
    /**
     * Whether NEW assignments may be made. False in a past semester, and in a future
     * one until the teacher opts in. Removing an existing assignment is never gated,
     * so only the assign direction of the per-student button is disabled.
     */
    canAdd?: boolean;
    /** Show the per-entity "Preview" action (teacher walkthrough registration). */
    showPreview?: boolean;
    /** documentId of the entity whose preview registration is being created. */
    previewLoadingDocumentId?: string;
  }>(),
  {
    canAdd: true,
    selectedName: undefined,
    showPreview: false,
    previewLoadingDocumentId: '',
  },
);

const emit = defineEmits<{
  (e: 'update:search', value: string): void;
  (e: 'toggle-expanded'): void;
  (e: 'select', entityDocumentId: string): void;
  (e: 'preview', entityDocumentId: string): void;
  (
    e: 'set-student-assignment',
    payload: { studentDocumentId: string; assigned: boolean },
  ): void;
}>();

const $q = useQuasar();
const { t } = useI18n();

const nameSortOrder = ref<'asc' | 'desc'>('asc');
const assignedSortOrder = ref<'assigned-first' | 'unassigned-first'>(
  'assigned-first',
);

// Dropped/declined/pending students are hidden until the teacher asks for them,
// and then listed last. They can be unassigned but not given new work.
const showInactive = ref(false);

const inactiveCount = computed(
  () =>
    (props.progressRows || []).filter((row) =>
      isInactiveEnrollment(row.enrollmentStatus),
    ).length,
);

function isInactiveRow(row: EntityProgressRow) {
  return isInactiveEnrollment(row.enrollmentStatus);
}

const sortedProgressRows = computed(() => {
  const rows = (props.progressRows || []).filter(
    (row) => showInactive.value || !isInactiveRow(row),
  );
  rows.sort((a, b) => {
    const aInactive = isInactiveRow(a) ? 1 : 0;
    const bInactive = isInactiveRow(b) ? 1 : 0;
    if (aInactive !== bInactive) return aInactive - bInactive;

    const aAssigned = a.assigned ? 1 : 0;
    const bAssigned = b.assigned ? 1 : 0;

    if (aAssigned !== bAssigned) {
      return assignedSortOrder.value === 'assigned-first'
        ? bAssigned - aAssigned
        : aAssigned - bAssigned;
    }

    const aName = schoolUserDisplayName(a.student, '').toLowerCase();
    const bName = schoolUserDisplayName(b.student, '').toLowerCase();
    if (aName === bName) return 0;

    return nameSortOrder.value === 'asc'
      ? aName.localeCompare(bName)
      : bName.localeCompare(aName);
  });
  return rows;
});

// Unassigning keeps the student's progress (it is paused, and restored on
// reassign), but a student mid-way through loses access, so confirm first.
function toggleAssignment(row: EntityProgressRow) {
  const studentDocumentId = row.student?.documentId || '';
  const assigned = !row.assigned;
  const send = () =>
    emit('set-student-assignment', { studentDocumentId, assigned });

  if (assigned || !hasStartedProgress(row.userProgress)) {
    send();
    return;
  }

  $q.dialog({
    title: t('SCHOOL_UNASSIGN_STARTED_TITLE'),
    message: t(
      row.userProgress?.completionDate
        ? 'SCHOOL_UNASSIGN_COMPLETED_CONFIRM'
        : 'SCHOOL_UNASSIGN_STARTED_CONFIRM',
      {
        name: schoolUserDisplayName(row.student),
        entity: props.selectedName || '',
      },
    ),
    cancel: t('CANCEL'),
    ok: { label: t('SCHOOL_UNASSIGN'), color: 'negative' },
    persistent: true,
  }).onOk(send);
}

function toggleNameSort() {
  nameSortOrder.value = nameSortOrder.value === 'asc' ? 'desc' : 'asc';
}

function toggleAssignedSort() {
  assignedSortOrder.value =
    assignedSortOrder.value === 'assigned-first'
      ? 'unassigned-first'
      : 'assigned-first';
}
</script>

<template>
  <q-card
    bordered
    class="entity-card"
  >
    <q-card-section class="column q-gutter-md">
      <div class="row items-center q-col-gutter-sm">
        <div class="col">
          <div class="text-h6">{{ $t(titleKey) }}</div>
          <div class="text-caption text-muted">
            {{ `${$t('TOTAL')} ${totalAssigned}` }}
          </div>
        </div>
        <div class="col-12 col-sm-5 col-md-4">
          <q-input
            :model-value="search"
            dense
            outlined
            clearable
            :debounce="300"
            :label="$t('SEARCH')"
            @update:model-value="emit('update:search', String($event ?? ''))"
          />
        </div>
      </div>

      <div
        v-if="entities.length"
        class="row q-col-gutter-sm"
      >
        <div class="col-12">
          <div class="row items-center q-mb-xs">
            <div class="col text-caption text-muted text-weight-medium">
              {{ $t(titleKey) }}
            </div>
            <div class="col-auto">
              <q-btn
                dense
                flat
                round
                color="primary"
                :icon="isExpanded ? mdiChevronUp : mdiChevronDown"
                @click="emit('toggle-expanded')"
              />
            </div>
          </div>
          <div
            class="row q-col-gutter-sm"
            :class="{ 'assigned-entities-grid': !isExpanded }"
          >
            <div
              v-for="entity in entities"
              :key="entity.documentId"
              class="col-12 col-md-6"
            >
              <q-card
                bordered
                class="cursor-pointer school-entity-card shadow-1"
                :class="{
                  'school-entity-card--selected':
                    selectedDocumentId === entity.documentId,
                }"
                @click="emit('select', entity.documentId)"
              >
                <q-card-section class="q-pa-sm">
                  <div class="text-subtitle2">{{ entity.name }}</div>
                  <div class="row items-center q-col-gutter-xs q-mt-xs">
                    <div class="col-auto">
                      <q-chip
                        dense
                        size="sm"
                        color="secondary"
                        text-color="dark"
                      >
                        {{ $t('SCHOOL_ASSIGNED_COUNT') }}:
                        {{ entity.assignedCount || 0 }}
                      </q-chip>
                    </div>
                    <div class="col-auto">
                      <q-chip
                        dense
                        size="sm"
                        color="positive"
                        text-color="white"
                      >
                        {{ $t('SCHOOL_COMPLETED_COUNT') }}:
                        {{ entity.completedCount || 0 }}
                      </q-chip>
                    </div>
                    <q-space />
                    <div
                      v-if="showPreview"
                      class="col-auto"
                    >
                      <q-btn
                        dense
                        no-caps
                        outline
                        size="sm"
                        color="primary"
                        :icon="mdiEyeOutline"
                        :label="$t('PREVIEW')"
                        :loading="
                          previewLoadingDocumentId === entity.documentId
                        "
                        @click.stop="emit('preview', entity.documentId)"
                      />
                    </div>
                  </div>
                </q-card-section>
              </q-card>
            </div>
          </div>
        </div>
      </div>

      <q-banner
        v-else
        :class="
          $q.dark.isActive ? 'bg-grey-9 text-grey-4' : 'bg-grey-2 text-grey-8'
        "
      >
        {{ $t('NO_DATA_AVAILIABLE') }}
      </q-banner>
    </q-card-section>

    <q-separator />

    <div
      v-if="selectedDocumentId"
      class="relative-position progress-pane"
    >
      <q-inner-loading :showing="isProgressLoading">
        <q-spinner-gears
          size="40px"
          color="primary"
        />
      </q-inner-loading>
      <q-list>
        <q-item>
          <q-item-section>
            <q-item-label class="text-subtitle2">
              {{ selectedName || $t(selectedFallbackKey) }}
            </q-item-label>
            <q-item-label caption>
              {{ `${$t('STUDENTS')} ${$t('ASSIGNMENTS')}` }}
            </q-item-label>
          </q-item-section>
          <q-item-section side>
            <div class="row items-center q-gutter-sm progress-sort-actions">
              <q-btn
                class="mobile-sort-btn"
                dense
                flat
                color="primary"
                :icon="
                  nameSortOrder === 'asc'
                    ? mdiSortAlphabeticalAscending
                    : mdiSortAlphabeticalDescending
                "
                :label="$t('SORT')"
                @click="toggleNameSort"
              />
              <q-btn
                class="mobile-sort-btn"
                dense
                flat
                color="primary"
                :icon="mdiSort"
                :label="
                  assignedSortOrder === 'assigned-first'
                    ? $t('SCHOOL_ASSIGNED_LABEL')
                    : $t('SCHOOL_NOT_ASSIGNED_LABEL')
                "
                @click="toggleAssignedSort"
              />
              <q-btn
                v-if="inactiveCount"
                class="mobile-sort-btn show-inactive-btn"
                dense
                flat
                color="primary"
                :icon="mdiAccountOffOutline"
                :label="
                  showInactive
                    ? $t('SCHOOL_HIDE_INACTIVE_STUDENTS')
                    : $t('SCHOOL_SHOW_INACTIVE_STUDENTS', {
                        count: inactiveCount,
                      })
                "
                @click="showInactive = !showInactive"
              />
            </div>
          </q-item-section>
        </q-item>
        <q-separator
          spaced
          inset
        />
        <transition-group
          name="student-row"
          tag="div"
        >
          <q-item
            v-for="(row, rowIndex) in sortedProgressRows"
            :key="row.student?.documentId || rowIndex"
            class="progress-row"
            :class="{ 'inactive-row': isInactiveRow(row) }"
          >
            <q-item-section class="progress-row-main-section">
              <q-item-label>{{
                schoolUserDisplayName(row.student)
              }}</q-item-label>
              <q-item-label caption>
                {{
                  $t('SCHOOL_ENROLLMENT_STATUS_LABEL', {
                    status: row.enrollmentStatus || '-',
                  })
                }}
              </q-item-label>
            </q-item-section>
            <q-item-section
              side
              class="progress-row-status-section"
            >
              <div class="progress-row-chips">
                <q-chip
                  v-if="row.userProgress?.completionDate"
                  color="positive"
                  text-color="white"
                  size="sm"
                >
                  {{ $t('SCHOOL_STATUS_COMPLETED') }}
                </q-chip>
                <q-chip
                  v-if="
                    hasStartedProgress(row.userProgress) &&
                    typeof row.userProgress?.score === 'number'
                  "
                  color="secondary"
                  text-color="dark"
                  size="sm"
                >
                  {{
                    $t('SCHOOL_SCORE_LABEL', { score: row.userProgress.score })
                  }}
                </q-chip>
                <q-chip
                  :color="row.assigned ? 'primary' : 'grey-6'"
                  text-color="dark"
                  size="sm"
                >
                  {{
                    row.assigned
                      ? $t('SCHOOL_ASSIGNED_LABEL')
                      : $t('SCHOOL_NOT_ASSIGNED_LABEL')
                  }}
                </q-chip>
                <q-btn
                  v-if="row.student?.documentId"
                  dense
                  flat
                  round
                  color="primary"
                  class="row-assignment-btn"
                  :disable="
                    !row.assigned && (canAdd === false || isInactiveRow(row))
                  "
                  :icon="
                    row.assigned
                      ? mdiAccountMinusOutline
                      : mdiAccountPlusOutline
                  "
                  @click="toggleAssignment(row)"
                >
                  <q-tooltip v-if="!row.assigned && canAdd === false">
                    {{ $t('SCHOOL_ASSIGN_LOCKED_SEMESTER') }}
                  </q-tooltip>
                  <q-tooltip v-else-if="!row.assigned && isInactiveRow(row)">
                    {{ $t('SCHOOL_ASSIGN_INACTIVE_STUDENT') }}
                  </q-tooltip>
                </q-btn>
              </div>
            </q-item-section>
          </q-item>
        </transition-group>
        <q-item v-if="!sortedProgressRows.length">
          <q-item-section>
            <q-item-label class="text-muted">{{
              $t('SCHOOL_NO_STUDENTS_FOUND_FOR_CLASS')
            }}</q-item-label>
          </q-item-section>
        </q-item>
      </q-list>
    </div>

    <q-banner
      v-else-if="entities.length"
      :class="
        $q.dark.isActive ? 'bg-grey-9 text-grey-4' : 'bg-grey-2 text-grey-8'
      "
    >
      {{ $t(selectPromptKey) }}
    </q-banner>
  </q-card>
</template>

<style scoped>
.entity-card {
  border-radius: 12px;
}

.assigned-entities-grid {
  max-height: 230px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}

.school-entity-card {
  transition: all 0.15s ease;
}

.school-entity-card:hover {
  border-color: var(--q-primary);
  transform: translateY(-1px);
}

.school-entity-card--selected {
  background: var(--surface-elevated);
  border: 2px solid var(--q-primary);
}

.progress-pane {
  background: var(--surface-card);
}

.student-row-enter-active,
.student-row-leave-active {
  transition: opacity 0.2s ease;
}

.student-row-move {
  transition: transform 0.28s ease;
}

.student-row-enter-from,
.student-row-leave-to {
  opacity: 0;
}

.progress-row-main-section {
  min-width: 0;
}

.inactive-row {
  opacity: 0.55;
}

.progress-row-main-section :deep(.q-item__label) {
  overflow-wrap: break-word;
}

.progress-row-status-section {
  flex: 0 1 auto;
}

/* Divider between students; the first row sits under the header separator. */
.progress-row + .progress-row {
  border-top: 1px solid var(--border-subtle);
}

/* Completed · Score · Assigned · toggle on one line; wraps right-aligned when narrow. */
.progress-row-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}

.progress-row-chips :deep(.q-chip) {
  margin: 0;
}

@media (max-width: 599px) {
  .entity-card :deep(.q-card__section) {
    padding: 12px;
  }

  .progress-sort-actions {
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  .mobile-sort-btn {
    min-width: 0;
  }

  .progress-pane :deep(.q-item) {
    align-items: center;
  }

  .progress-row-status-section {
    width: auto;
    max-width: 60%;
    margin-top: 0;
  }
}
</style>
